import { ThemeProvider } from "@emotion/react";
import {
  AppBar,
  Box,
  createTheme,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthRoute, SFrame } from "components";
import { STheme } from "components/styles";
import { useRecoilValue } from "recoil";
import { authState } from "states";
import { SBar, SGyomuMenu } from "components/parts";
import { B000010Logout } from "clients/rest";
import { getLogoText } from "services";

type Props = Partial<{
  children: ReactNode;
  page: ReactNode;
}>;

export const B000020 = (props: Props) => {
  return (
    <AuthRoute>
      <Page {...props} />
    </AuthRoute>
  );
};

const Page = ({ children, page }: Props) => {
  const navigate = useNavigate();
  const auth = useRecoilValue(authState);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    setOpenDrawer(!children && !page);
  }, [children, page]);

  return (
    <ThemeProvider theme={theme}>
      <Box display="grid" gridTemplateColumns={"280px 1fr"} gap="0px">
        <Box gridColumn="span 2">
          <SBar
            auth={auth}
            onDrawer={() => setOpenDrawer(true)}
            onMenu={() => {
              B000010Logout()
                .then(() => {
                  navigate("/b000010");
                })
                .catch(() => {
                  navigate("/b000010");
                });
            }}
          />
        </Box>
        <Drawer
          variant="temporary"
          anchor="left"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
        >
          <AppBar position="static">
            <Toolbar variant="dense">
              <IconButton
                color="inherit"
                onClick={() => setOpenDrawer(false)}
                edge="start"
              >
                <ChevronLeft fontSize="large" />
              </IconButton>
              <Typography
                color="inherit"
                style={{ fontSize: "1.4rem", fontWeight: "bold" }}
              >
                {getLogoText()}
              </Typography>
            </Toolbar>
          </AppBar>
          <SFrame marginRight="5px">
            <SGyomuMenu closeDrawer={() => setOpenDrawer(false)} />
          </SFrame>
        </Drawer>
        <Box justifySelf="start" marginRight="5px">
          {typeof children === "undefined" ? page : children}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

const theme = createTheme(
  {
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            fontSize: "1.4rem",
            paddingLeft: "0px",
          },
          colorPrimary: {
            backgroundColor: STheme.frames.bar,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: "0px",
            paddingLeft: "5px",
            "@media (min-width: 600px)": {
              minHeight: "0px",
              paddingLeft: "5px",
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root: {
            minWidth: "280px",
            width: "auto",
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: "280px",
              boxSizing: "border-box",
              "& .MuiPaper-root": {
                height: "auto",
              },
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
    },
  },
  { ...STheme }
);
